/**
 * Renew — loads the previous term and hands it to the new-subscription screen
 * as a prefill, so there is exactly one implementation of the money maths,
 * the validation and the receipt.
 *
 * The start date is resolved by `renewalStartDate`: the day after the current
 * end date if they are still active (no lost days), otherwise today (no
 * backdating days already gone). Staff can override it — the field is visible.
 *
 * The PRICE, however, comes from today's catalog, not the old subscription's
 * snapshot. A renewal is a new sale at the current rate; the snapshot exists to
 * protect the historical record, not to freeze a price forever.
 */
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { db } from '../db/db';
import NewSubscription, { type Prefill } from './NewSubscription';
import { renewalStartDate, todayISO } from '@shared/dates';
import { TERM_MONTHS, type TermMonths } from '@shared/pricing';

export default function RenewSubscription() {
  const { subscriptionId = '' } = useParams();
  const [prefill, setPrefill] = useState<Prefill | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;

    (async () => {
      const subscription = await db.subscriptions.get(subscriptionId);
      if (!subscription || subscription.deleted_at !== null) {
        if (!cancelled) setError('That subscription no longer exists.');
        return;
      }
      const member = await db.members.get(subscription.member_id);
      if (!member || member.deleted_at !== null) {
        if (!cancelled) setError('That member no longer exists.');
        return;
      }

      if (!cancelled) {
        setPrefill({
          member,
          classKey: subscription.class_key,
          // Old rows may carry a term the schedule no longer sells; fall back
          // to 1 month rather than showing a price of zero.
          months: TERM_MONTHS.includes(subscription.months as TermMonths)
            ? (subscription.months as TermMonths)
            : 1,
          private: subscription.private,
          // The coach carries across so a renewal keeps the member with the
          // person they have been training with. Staff can still change it.
          privateCoach: subscription.private_coach,
          // Club membership is read fresh from the member, not the old
          // subscription — if they joined the club since, they get the rate now.
          clubMember: member.club_member,
          startDate: renewalStartDate(subscription.end_date, todayISO()),
          isRenewal: true,
        });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [subscriptionId]);

  if (error) {
    return (
      <div className="mx-auto max-w-lg px-5 py-20 text-center">
        <h1 className="mb-3 text-3xl text-brand">Cannot renew</h1>
        <p className="mb-6 text-white/50">{error}</p>
        <Link to="/renewals" className="btn">
          Back to renewals
        </Link>
      </div>
    );
  }

  if (!prefill) return <p className="py-20 text-center text-white/30">Loading…</p>;

  // Keyed so switching between two renewals rebuilds the form state cleanly.
  return <NewSubscription key={subscriptionId} prefill={prefill} />;
}
